import { MOBILE_BREAKPOINT, WORKSPACE_SPLIT_MIN } from '@common/theme';

/**
 * The agent workspace's view switch — pure, so the rules are testable without a
 * browser.
 *
 *  - `view` is what the user asked for (the URL `?view=` on the page, local state
 *    in the extension dialog): chat only, form only, or both side by side.
 *  - Defaults: a NEW agent opens on the chat, an existing one on both.
 *  - "Both" needs a frame of at least WORKSPACE_SPLIT_MIN px. Below that a `both`
 *    request renders as tabs WITHOUT changing the request (widening the window
 *    brings Both back); the first tab is the chat for a new agent and the form
 *    for an existing one.
 *  - Below MOBILE_BREAKPOINT the frame is "mobile": still one pane with tabs.
 */
export type WorkspaceView = 'chat' | 'form' | 'both';
export type WorkspaceMode = 'new' | 'edit';
export type WorkspacePane = 'chat' | 'form';

/** The `?view=` value, or null when it is missing / not one of ours. */
export function parseView(raw: string | null | undefined): WorkspaceView | null {
  return raw === 'chat' || raw === 'form' || raw === 'both' ? raw : null;
}

export function defaultView(mode: WorkspaceMode): WorkspaceView {
  return mode === 'new' ? 'chat' : 'both';
}

export interface WorkspaceLayout {
  /** The view in effect (the request, or the mode's default). */
  view: WorkspaceView;
  /** Two panes side by side. */
  split: boolean;
  /** One pane at a time: the one showing. Meaningless when `split`. */
  pane: WorkspacePane;
  /** The frame is wide enough to offer "Both" in the switch. */
  offerBoth: boolean;
  /** The frame is narrower than MOBILE_BREAKPOINT. */
  mobile: boolean;
}

/**
 * @param requested the `?view=` value (null = default)
 * @param frameWidth the width of the frame the workspace fills, in px
 */
export function resolveLayout(mode: WorkspaceMode, requested: WorkspaceView | null, frameWidth: number): WorkspaceLayout {
  const view = requested ?? defaultView(mode);
  const offerBoth = frameWidth >= WORKSPACE_SPLIT_MIN;
  const split = view === 'both' && offerBoth;
  const pane: WorkspacePane = view === 'both' ? (mode === 'new' ? 'chat' : 'form') : view;
  return { view, split, pane, offerBoth, mobile: frameWidth < MOBILE_BREAKPOINT };
}

/** The switch's options for a frame: Chat · Form (· Both when it fits). */
export function viewOptions(offerBoth: boolean): WorkspaceView[] {
  return offerBoth ? ['chat', 'form', 'both'] : ['chat', 'form'];
}

/** The option the switch shows as selected. */
export function selectedOption(layout: WorkspaceLayout): WorkspaceView {
  return layout.split ? 'both' : layout.pane;
}

/**
 * The view to show right after an agent is created: keep the conversation on
 * screen — Both when the frame fits it (chat + the new agent's form), else the chat.
 */
export function viewAfterCreate(offerBoth: boolean): WorkspaceView {
  return offerBoth ? 'both' : 'chat';
}

/** An agent's own page (`/agents/:agentId`) — the workspace in Edit. */
export const agentEditPath = (agentId: string) => `/agents/${encodeURIComponent(agentId)}`;

/** An agent's conversations page. */
export const agentConversationsPath = (agentId: string) => `${agentEditPath(agentId)}/conversations`;

/** The New-agent page. */
export const NEW_AGENT_PATH = '/agents/new';
